import { Button } from "@gsio/component-design-system";
import { type FormEvent, useEffect, useState } from "react";
import {
  BusinessEngineApiError,
  type BusinessProfileClient,
  businessEngineClient,
} from "./api";
import {
  type BusinessProfile,
  buildRevenuePlan,
  GSIO_REVENUE_PLAN_POLICY_VERSION,
  profileCompletion,
  validateBusinessProfileInput,
} from "./business-profile";
import {
  type CustomerSection,
  createBusinessProfileChangeHandler,
  createConfigureClickHandler,
  createCustomerNavClickHandler,
  createCustomerNavigateFn,
  createCustomerNavigateHandler,
  createEmailInputChangeHandler,
  createInitialCustomerSection,
  createLeadSourcesChangeHandler,
  createOptionalNumberChangeHandler,
  createPreferredContactMethodChangeHandler,
  createReloadPageHandler,
  createResetEmailSignInHandler,
  createSectionPopStateHandler,
  createServicesChangeHandler,
  createSetupNavigateHandler,
  createTextFieldChangeHandler,
} from "./customer-app-forms";
import {
  CustomerBrand,
  CustomerGrid,
  CustomerHeader,
  CustomerHero,
  CustomerLoading,
  CustomerLoginForm,
  CustomerMain,
  CustomerMark,
  CustomerNav,
  CustomerPanel,
  CustomerShell,
  EmptyEvidence,
  ExtraTasks,
  Eyebrow,
  FormMessage,
  InlineButton,
  OwnerControl,
  PlanCard,
  PlanList,
  ProgressHeader,
  ProgressTrack,
  SaveBar,
  ServiceTags,
  SetupForm,
  SetupIntro,
  WideField,
} from "./styles/customer";

const listValue = (values: string[]) => values.join("\n");

export { optionalNumber, parseList } from "./customer-app-forms";

function money(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function CustomerChrome({
  profile,
  section,
  onNavigate,
  children,
}: {
  profile: BusinessProfile;
  section: CustomerSection;
  onNavigate: (section: CustomerSection) => void;
  children: React.ReactNode;
}) {
  return (
    <CustomerShell>
      <CustomerHeader>
        <a href="/" onClick={createCustomerNavClickHandler(onNavigate, "plan")}>
          <CustomerMark aria-hidden="true">G</CustomerMark>
          <CustomerBrand>
            <strong>{profile.businessName}</strong>
            <small>{profile.location}</small>
          </CustomerBrand>
        </a>
      </CustomerHeader>
      <CustomerMain>{children}</CustomerMain>
      <CustomerNav aria-label="Customer control panel">
        <a
          href="/"
          aria-current={section === "plan" ? "page" : undefined}
          onClick={createCustomerNavClickHandler(onNavigate, "plan")}
        >
          Revenue plan
        </a>
        <a
          href="/setup"
          aria-current={section === "setup" ? "page" : undefined}
          onClick={createCustomerNavClickHandler(onNavigate, "setup")}
        >
          Business setup
        </a>
      </CustomerNav>
    </CustomerShell>
  );
}

function RevenuePlan({
  profile,
  notice,
  onConfigure,
}: {
  profile: BusinessProfile;
  notice: string;
  onConfigure: (fieldId?: string) => void;
}) {
  const progress = profileCompletion(profile);
  const actions = buildRevenuePlan(profile);
  const visibleActions = actions.slice(0, 3);
  return (
    <>
      {notice && (
        <FormMessage role="status" aria-live="polite">
          {notice}
        </FormMessage>
      )}
      <CustomerHero>
        <div>
          <Eyebrow data-policy-version={GSIO_REVENUE_PLAN_POLICY_VERSION}>
            Revenue plan · owner-controlled
          </Eyebrow>
          <h1>Make the next week more profitable.</h1>
          <p>
            Start with the work Global Scale Insights already earns through its
            founder network. The plan will get more specific only when real
            engagement evidence supports it.
          </p>
        </div>
        <OwnerControl>
          <strong>You approve every proposal</strong>
          <span>No automatic prices, commitments, promises, or messages.</span>
        </OwnerControl>
      </CustomerHero>

      <CustomerGrid>
        <div>
          <CustomerPanel>
            <ProgressHeader>
              <div>
                <h2>Your best next moves</h2>
                <p>Ordered from known facts—never fabricated revenue.</p>
              </div>
              <strong>{progress.percent}%</strong>
            </ProgressHeader>
            <ProgressTrack
              role="progressbar"
              aria-label="Business setup completeness"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progress.percent}
            >
              <i style={{ width: `${progress.percent}%` }} />
            </ProgressTrack>
            <PlanList>
              {visibleActions.map((action, index) => (
                <PlanCard key={action.id} $kind={action.kind}>
                  <span aria-hidden="true">{index + 1}</span>
                  <div>
                    <h3>{action.title}</h3>
                    <p>{action.detail}</p>
                  </div>
                  <button
                    type="button"
                    onClick={createConfigureClickHandler(
                      onConfigure,
                      action.fieldId,
                    )}
                  >
                    {action.actionLabel}
                  </button>
                </PlanCard>
              ))}
            </PlanList>
            {actions.length > visibleActions.length && (
              <ExtraTasks>
                {actions.length - visibleActions.length} more safe actions will
                follow these.
              </ExtraTasks>
            )}
          </CustomerPanel>
        </div>

        <div>
          <CustomerPanel>
            <h2>Business foundation</h2>
            <p>
              {progress.complete} of {progress.total} planning inputs are ready.
            </p>
            <ServiceTags aria-label="Configured services">
              {profile.services.map((service) => (
                <span key={service}>{service}</span>
              ))}
            </ServiceTags>
            <InlineButton
              type="button"
              onClick={createConfigureClickHandler(onConfigure)}
            >
              Manage setup
            </InlineButton>
          </CustomerPanel>
          <CustomerPanel>
            <h2>Revenue evidence</h2>
            {profile.monthlyRevenueTarget ? (
              <p>
                Owner-entered monthly target:{" "}
                {money(profile.monthlyRevenueTarget)}
              </p>
            ) : (
              <p>No revenue target entered yet.</p>
            )}
            <EmptyEvidence>
              <strong>No closed engagements connected yet</strong>
              <p>
                The product will not call a service line profitable or recommend
                a price until delivery hours and direct engagement costs are
                available.
              </p>
            </EmptyEvidence>
          </CustomerPanel>
        </div>
      </CustomerGrid>
    </>
  );
}

function BusinessSetup({
  draft,
  saving,
  error,
  saved,
  onChange,
  onSubmit,
}: {
  draft: BusinessProfile;
  saving: boolean;
  error: string;
  saved: string;
  onChange: (profile: BusinessProfile) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const change = createBusinessProfileChangeHandler(draft, onChange);
  return (
    <>
      <SetupIntro>
        <Eyebrow>Business setup</Eyebrow>
        <h1>Give the Revenue Plan real boundaries.</h1>
        <p>
          Unknown values stay blank. GSIO approves every proposal and customer
          commitment, especially on-prem deployment and support work.
        </p>
      </SetupIntro>
      <SetupForm onSubmit={onSubmit} noValidate>
        {error && (
          <FormMessage $error role="alert">
            {error}
          </FormMessage>
        )}
        {saved && (
          <FormMessage role="status" aria-live="polite">
            {saved}
          </FormMessage>
        )}
        <fieldset disabled={saving}>
          <legend>Business and offer</legend>
          <label>
            Business name
            <input
              name="businessName"
              value={draft.businessName}
              maxLength={100}
              required
              onChange={createTextFieldChangeHandler(change, "businessName")}
            />
          </label>
          <label>
            Operating base
            <input
              name="location"
              value={draft.location}
              maxLength={120}
              required
              onChange={createTextFieldChangeHandler(change, "location")}
            />
          </label>
          <WideField>
            Market focus
            <span>
              Describe the kind of organization served; do not enter individual
              customer names.
            </span>
            <input
              name="marketFocus"
              value={draft.marketFocus}
              maxLength={180}
              required
              onChange={createTextFieldChangeHandler(change, "marketFocus")}
            />
          </WideField>
          <WideField>
            Services
            <span>
              One service line per row. Every service remains proposal-only.
            </span>
            <textarea
              name="services"
              value={listValue(draft.services)}
              required
              onChange={createServicesChangeHandler(change)}
            />
          </WideField>
          <WideField>
            Current lead sources
            <span>
              Use a general label; do not store customer or individual contact
              names.
            </span>
            <textarea
              name="leadSources"
              value={listValue(draft.leadSources)}
              required
              onChange={createLeadSourcesChangeHandler(change)}
            />
          </WideField>
        </fieldset>

        <fieldset disabled={saving}>
          <legend>Money and capacity</legend>
          <label>
            Monthly revenue target
            <span>Owner-entered goal, not a forecast.</span>
            <input
              id="monthlyRevenueTarget"
              name="monthlyRevenueTarget"
              type="number"
              inputMode="decimal"
              min="1"
              step="100"
              placeholder="Leave blank until decided"
              value={draft.monthlyRevenueTarget ?? ""}
              onChange={createOptionalNumberChangeHandler(
                change,
                "monthlyRevenueTarget",
              )}
            />
          </label>
          <label>
            Weekly delivery capacity
            <span>
              Hours available for customer work—not total working hours.
            </span>
            <input
              id="weeklyCapacityHours"
              name="weeklyCapacityHours"
              type="number"
              inputMode="decimal"
              min="1"
              max="168"
              step="1"
              placeholder="Hours per week"
              value={draft.weeklyCapacityHours ?? ""}
              onChange={createOptionalNumberChangeHandler(
                change,
                "weeklyCapacityHours",
              )}
            />
          </label>
          <label>
            Minimum engagement value
            <span>A floor to review before accepting a small engagement.</span>
            <input
              id="minimumEngagementValue"
              name="minimumEngagementValue"
              type="number"
              inputMode="decimal"
              min="1"
              step="500"
              placeholder="Leave blank until decided"
              value={draft.minimumEngagementValue ?? ""}
              onChange={createOptionalNumberChangeHandler(
                change,
                "minimumEngagementValue",
              )}
            />
          </label>
        </fieldset>

        <fieldset disabled={saving}>
          <legend>Customer intake</legend>
          <WideField>
            Preferred first contact
            <span>One reliable front door reduces missed referrals.</span>
            <select
              id="preferredContactMethod"
              name="preferredContactMethod"
              value={draft.preferredContactMethod}
              onChange={createPreferredContactMethodChangeHandler(change)}
            >
              <option value="">Choose later</option>
              <option value="scoping_request">Scoping-request form</option>
              <option value="call">Intro call</option>
              <option value="email">Email</option>
            </select>
          </WideField>
        </fieldset>

        <SaveBar>
          <p>Saving creates a versioned, auditable business configuration.</p>
          <button type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save and view Revenue Plan"}
          </button>
        </SaveBar>
      </SetupForm>
    </>
  );
}

export function CustomerApp({
  client = businessEngineClient,
}: {
  client?: BusinessProfileClient;
}) {
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [draft, setDraft] = useState<BusinessProfile | null>(null);
  const [section, setSection] = useState<CustomerSection>(
    createInitialCustomerSection,
  );
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadStatus, setLoadStatus] = useState<number | null>(null);
  const [email, setEmail] = useState("");
  const [emailState, setEmailState] = useState<"idle" | "sending" | "sent">(
    "idle",
  );
  const [emailMessage, setEmailMessage] = useState("");
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    client
      .getBusinessProfile(controller.signal)
      .then((loaded) => {
        setLoadStatus(null);
        setProfile(loaded);
        setDraft(loaded);
      })
      .catch((reason) => {
        if (reason instanceof DOMException && reason.name === "AbortError")
          return;
        setLoadStatus(
          reason instanceof BusinessEngineApiError ? reason.status : null,
        );
        setError(
          reason instanceof Error
            ? reason.message
            : "Unable to load the GSIO control panel",
        );
      });
    const onPopState = createSectionPopStateHandler(setSection);
    addEventListener("popstate", onPopState);
    return () => {
      controller.abort();
      removeEventListener("popstate", onPopState);
    };
  }, [client]);

  const navigate = createCustomerNavigateFn(setSection);

  const requestEmailSignIn = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submitted = new FormData(event.currentTarget).get("email");
    const address = typeof submitted === "string" ? submitted.trim() : "";
    if (!address?.includes("@") || address.length > 254) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setEmailState("sending");
    setEmailError("");
    try {
      const result = await client.requestEmailSignIn(address.toLowerCase());
      setEmailMessage(result.message);
      setEmailState("sent");
    } catch (reason) {
      // Surface 4xx guidance (wrong domain, malformed address) verbatim; keep
      // 5xx detail generic so provider internals stay server-side.
      setEmailError(
        reason instanceof BusinessEngineApiError &&
          reason.status >= 400 &&
          reason.status < 500
          ? reason.message
          : "We could not request a sign-in link right now. Please try again.",
      );
      setEmailState("idle");
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draft) return;
    setError("");
    setSaved("");
    const invalid = validateBusinessProfileInput(draft);
    if (invalid) {
      setError(invalid);
      return;
    }
    setSaving(true);
    try {
      const updated = await client.saveBusinessProfile(draft);
      setProfile(updated);
      setDraft(updated);
      setSaved("Business setup saved.");
      navigate("plan");
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Business setup could not be saved",
      );
    } finally {
      setSaving(false);
    }
  };

  const content =
    profile && draft ? (
      section === "setup" ? (
        <BusinessSetup
          draft={draft}
          saving={saving}
          error={error}
          saved={saved}
          onChange={setDraft}
          onSubmit={submit}
        />
      ) : (
        <RevenuePlan
          profile={profile}
          notice={saved}
          onConfigure={createSetupNavigateHandler({ setSaved, navigate })}
        />
      )
    ) : null;

  if (!profile || !draft) {
    const needsSignIn = loadStatus === 401;
    return (
      <CustomerShell>
        <CustomerLoading>
          <div>
            <span aria-hidden="true">G</span>
            <h1>
              {needsSignIn
                ? "Sign in to GSIO"
                : error
                  ? "Control panel unavailable"
                  : "Opening GSIO"}
            </h1>
            <p>
              {needsSignIn
                ? "Enter the email address invited for Global Scale Insights. We’ll email a secure sign-in link—no password or separate account needed."
                : error || "Loading the owner-configured Revenue Plan…"}
            </p>
            {needsSignIn ? (
              emailState === "sent" ? (
                <CustomerLoginForm>
                  <FormMessage role="status" aria-live="polite">
                    {emailMessage}
                  </FormMessage>
                  <button
                    type="button"
                    onClick={createResetEmailSignInHandler({
                      setEmail,
                      setEmailMessage,
                      setEmailState,
                    })}
                  >
                    Use a different email
                  </button>
                </CustomerLoginForm>
              ) : (
                <CustomerLoginForm onSubmit={requestEmailSignIn} noValidate>
                  <label htmlFor="customer-sign-in-email">Email address</label>
                  <input
                    id="customer-sign-in-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    enterKeyHint="send"
                    value={email}
                    disabled={emailState === "sending"}
                    aria-describedby={
                      emailError ? "email-sign-in-error" : undefined
                    }
                    onChange={createEmailInputChangeHandler(setEmail)}
                  />
                  {emailError ? (
                    <FormMessage id="email-sign-in-error" $error role="alert">
                      {emailError}
                    </FormMessage>
                  ) : null}
                  <button type="submit" disabled={emailState === "sending"}>
                    {emailState === "sending"
                      ? "Sending secure link…"
                      : "Email me a sign-in link"}
                  </button>
                </CustomerLoginForm>
              )
            ) : error ? (
              <Button type="button" onClick={createReloadPageHandler()}>
                Try again
              </Button>
            ) : null}
          </div>
        </CustomerLoading>
      </CustomerShell>
    );
  }

  return (
    <CustomerChrome
      profile={profile}
      section={section}
      onNavigate={createCustomerNavigateHandler({ setSaved, navigate })}
    >
      {content}
    </CustomerChrome>
  );
}
